import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import EventCard from "@/components/feature/EventCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchEvents } from "@/mocks/events";

const hotKeywords = ["湖人", "勇士", "曼城", "利物浦", "皇马", "巴萨", "LPL", "KPL", "NBA", "英超"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  usePageMeta(
    query ? `“${query}”搜索结果 - jrs直播` : "赛事搜索 - jrs直播",
    query
      ? `jrs直播为你找到与“${query}”相关的体育赛事直播，涵盖篮球、足球、电竞等项目，可查看实时比分与直播入口。`
      : "在 jrs直播搜索篮球、足球、电竞等体育赛事，快速找到你关心的比赛与球队。",
    "赛事搜索,jrs直播,体育直播,篮球直播,足球直播,电竞直播",
  );

  const results = useMemo(() => searchEvents(query), [query]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
      <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">赛事搜索</span>
      </nav>

      <h1 className="mt-5 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
        {query ? `“${query}”的搜索结果` : "赛事搜索"}
      </h1>
      <p className="mt-2 text-sm text-foreground-600 md:text-base">
        输入球队、联赛或赛事名称，快速定位你关心的比赛。（当前为前端演示数据搜索）
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-center"
        role="search"
      >
        <div className="flex flex-1 items-center gap-2.5 rounded-full border border-background-300 bg-background-50 px-4 py-3">
          <i className="ri-search-line text-lg text-foreground-500" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索赛事名称或球队，如 湖人、曼城、T1"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            aria-label="搜索赛事名称或球队"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-500">热门搜索：</span>
        {hotKeywords.map((kw) => (
          <button
            key={kw}
            type="button"
            onClick={() => {
              setKeyword(kw);
              setSearchParams({ q: kw });
            }}
            className="rounded-full border border-background-300 bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-500/50 hover:text-primary-400 whitespace-nowrap cursor-pointer"
          >
            {kw}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {query ? (
          <>
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-semibold text-foreground-900">{results.length}</span> 场相关赛事
            </p>
            {results.length > 0 ? (
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {results.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-emotion-sad-line text-3xl" />
                </span>
                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                  没有找到相关赛事
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-600">
                  换个关键词试试，或者浏览以下热门分类，发现更多精彩赛事。
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/category/basketball"
                    className="rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                  >
                    浏览篮球直播
                  </Link>
                  <Link
                    to="/category/football"
                    className="rounded-md border border-background-300 bg-background-100 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-400 whitespace-nowrap cursor-pointer"
                  >
                    浏览足球直播
                  </Link>
                  <Link
                    to="/category/esports"
                    className="rounded-md border border-background-300 bg-background-100 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-400 whitespace-nowrap cursor-pointer"
                  >
                    浏览电竞直播
                  </Link>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
              输入关键词开始搜索
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              支持按球队、联赛、项目与赛区搜索，例如“湖人”“曼城”“LPL”。
            </p>
          </div>
        )}
      </div>
    </section>
  );
}